import React, { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { INLINE_LINK_STYLE } from '../../utils/styleHelpers';

export interface FileDropAreaProps {
  instructions: string;
  subInstructions?: string;
  buttonText: string;
  allowedFileTypes?: string[];
  multiple?: boolean;
  // A link reads as part of the instructions ("Drag a file here or browse"); a button sits under them.
  browseAs?: 'button' | 'link';
  disabled?: boolean;
  // Clears the picker after each pick, so the same file, as after removing it, can be picked again.
  allowRepick?: boolean;
  testId?: string;
  onFileDrop: (files: File[]) => void;
}

const MAX_FILES = 10;

const FileDropArea: React.FC<FileDropAreaProps> = ({
  onFileDrop,
  instructions,
  subInstructions,
  buttonText,
  allowedFileTypes,
  multiple = true,
  browseAs = 'button',
  disabled = false,
  allowRepick = false,
  testId
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handOver = (files: FileList | null) => {
    if (disabled || !files || files.length === 0) return;
    onFileDrop(Array.from(files).slice(0, MAX_FILES));
  };

  const handleDrag = (dragging: boolean) => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(dragging && !disabled);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    handleDrag(false)(e);
    handOver(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handOver(e.target.files);
    if (allowRepick) e.target.value = '';
  };

  const browse = () => fileInputRef.current?.click();

  return (
    <Paper
      elevation={0}
      data-testid={testId}
      sx={{
        p: 1,
        mb: 1,
        border: isDragging ? '2px dashed #666' : '2px dashed #ccc',
        borderRadius: 2,
        backgroundColor: isDragging ? '#e8e8e8' : '#fafafa',
        transition: 'all 0.3s ease',
        cursor: 'default',
        opacity: 1,
        '& .MuiPaper-root': {
          boxShadow: 'none'
        }
      }}
      onDragEnter={handleDrag(true)}
      onDragLeave={handleDrag(false)}
      onDragOver={handleDrag(true)}
      onDrop={handleDrop}>
      <input
        type="file"
        multiple={multiple}
        accept={allowedFileTypes?.join(',')}
        ref={fileInputRef}
        data-testid={testId && `${testId}-input`}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1,
          paddingTop: 2,
          paddingBottom: 2
        }}>
        <Typography
          variant="inherit"
          gutterBottom>
          {instructions}
          {browseAs === 'link' && (
            <>
              {' '}
              <button
                type="button"
                className={INLINE_LINK_STYLE}
                disabled={disabled}
                onClick={browse}>
                {buttonText}
              </button>
            </>
          )}
        </Typography>
        {browseAs === 'button' && (
          <Button
            variant="contained"
            component="span"
            size="small"
            disabled={disabled}
            sx={{
              mt: 1,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}
            onClick={browse}>
            {buttonText}
          </Button>
        )}
        <Typography
          variant="inherit"
          sx={{
            mt: 1,
            fontSize: '0.75rem'
          }}
          gutterBottom>
          {subInstructions}
        </Typography>
      </Box>
    </Paper>
  );
};

export default FileDropArea;
